/**
 * `/about` — corpus state.
 *
 * Everything on this screen is measured live. It exists because staleness
 * should be visible rather than guessed at (§13), and because the two facts
 * that most shape what the rest of the app can do -- that no document
 * reaches `verified`, and that `concept` is NULL everywhere -- are worth
 * stating plainly somewhere rather than only implying.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { AboutResponse } from '@server/sql/about.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, percent, percent0, timestamp } from '~/i18n/format.ts'
import { Badge, Bar, Label, Panel, Skeleton, bandOf } from '~/components/ui/primitives.tsx'
import { Command, ErrorState } from '~/components/ui/empty.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { useDocumentTitle } from '~/lib/title.ts'

export function AboutScreen() {
  const { t, locale, v } = useT()
  useDocumentTitle(t('nav.about'))
  const superadmin = useSuperadmin()
  const { data, error, refetch } = useQuery({
    queryKey: keys.about,
    queryFn: () => api<AboutResponse>('/api/about'),
    staleTime: 60_000,
  })

  if (error) {
    const e = error as ApiError
    return (
      <Column>
        <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
          onRetry={() => void refetch()} retryLabel={t('action.retry')} />
      </Column>
    )
  }
  if (!data) return <Column><Skeleton className="h-96 w-full" /></Column>

  const missingIndexes = data.indexes.filter((i) => !i.present)
  const emptyTables = data.tables.filter((tb) => tb.empty)

  return (
    <Column>
      <header className="border-b border-[var(--border)] pb-3">
        <h1 className="text-[length:var(--text-display)] font-semibold tracking-tight">
          {t('about.title')}
        </h1>
        <p className="tnum mt-0.5 text-[length:var(--text-data)] text-[var(--text-muted)]">
          {count(data.complete.documents, locale)} {t('about.complete').toLowerCase()}
          <Sep />
          {count(data.complete.companies, locale)} {t('nav.results').toLowerCase()}
          <Sep />
          {count(data.complete.pages, locale)} {t('viewer.rail').toLowerCase()}
          {data.lastUpdatedAt && (
            <>
              <Sep />
              <span className="text-[var(--text-faint)]">
                {timestamp(data.lastUpdatedAt, locale)}
              </span>
            </>
          )}
        </p>
      </header>

      <div className="grid gap-5 pt-4 lg:grid-cols-2">
        {/* --- what "complete" means ---------------------------------- */}
        <section className="lg:col-span-2">
          <Label className="mb-1.5">{t('about.complete')}</Label>
          <p className="max-w-[92ch] text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)] text-[var(--text-muted)]">
            {t('about.completeWhy')}
          </p>
        </section>

        {/* --- what is not ------------------------------------------- */}
        <section>
          <Label className="mb-1.5">{t('about.incomplete')}</Label>
          <Panel className="divide-y divide-[var(--border)]">
            {data.incomplete.length === 0 ? (
              <Row label={t('common.none')} value="" />
            ) : (
              data.incomplete.map((r) => (
                <Row
                  key={r.status}
                  label={v('docStatus', r.status) ?? <Untranslated>{r.status}</Untranslated>}
                  value={count(r.documents, locale)}
                />
              ))
            )}
          </Panel>
          <p className="mt-1 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('about.incompleteWhy')}
          </p>
        </section>

        {/* --- quality ------------------------------------------------
            Pass rates, the review queue and the per-check breakdown are the
            whole of what the default view hides, so both sections go
            together. What is left on this page is what the corpus *contains*
            rather than how it scored. */}
        {superadmin && <section>
          <Label className="mb-1.5">{t('quality.title')}</Label>
          <Panel className="divide-y divide-[var(--border)]">
            <div className="flex items-center justify-between gap-3 px-3 py-1.5">
              <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
                {t('quality.passRate')}
              </span>
              <span className="flex items-center gap-2">
                <Bar value={data.quality.passRate} />
                <span
                  className={cn(
                    'tnum text-[length:var(--text-data)] font-medium',
                    bandOf(data.quality.passRate) === 'pass' ? 'text-[var(--pass)]'
                      : bandOf(data.quality.passRate) === 'warn' ? 'text-[var(--warn)]'
                      : 'text-[var(--fail)]',
                  )}
                >
                  {t('quality.passRateOf', {
                    pct: percent(data.quality.passRate, locale),
                    passed: count(data.quality.passed, locale),
                    total: count(data.quality.checks, locale),
                  })}
                </span>
              </span>
            </div>
            <Row label={t('quality.openReview', { n: '' })} value={count(data.quality.openReview, locale)} />
            <Row
              label={t('quality.xbrl')}
              value={data.quality.xbrlAgreement === null ? '—' : percent(data.quality.xbrlAgreement, locale)}
            />
            <Row
              label={t('about.withFailures')}
              value={`${count(data.quality.documentsWithFailures, locale)} ${t('common.of')} ${count(data.complete.documents, locale)}`}
            />
          </Panel>
        </section>}

        {/* --- review queue ------------------------------------------- */}
        {superadmin && <section className="lg:col-span-2">
          <Label className="mb-1.5">{t('quality.byCheck')}</Label>
          <Table>
            <thead>
              <Tr>
                <Th>{t('quality.detail')}</Th>
                <Th numeric>{t('docs.review')}</Th>
                <Th numeric>%</Th>
              </Tr>
            </thead>
            <tbody>
              {data.reviewByReason.map((r) => {
                const total = data.reviewByReason.reduce((n, x) => n + x.count, 0)
                const name = /^(error|warn):\s*(.+)$/.exec(r.reason)
                const severity = name?.[1]
                const check = name?.[2] ?? r.reason
                return (
                  <Tr key={r.reason}>
                    <Td>
                      <span className="flex items-baseline gap-2">
                        {severity && (
                          <Badge state={severity === 'warn' ? 'warn' : 'fail'}>
                            {severity === 'warn' ? t('quality.severity.warn') : t('quality.severity.error')}
                          </Badge>
                        )}
                        {v('checkName', check) ?? v('reviewReason', check) ?? <Untranslated>{check}</Untranslated>}
                      </span>
                    </Td>
                    <Td numeric>{count(r.count, locale)}</Td>
                    <Td numeric className="text-[var(--text-muted)]">
                      {percent0(total ? r.count / total : null, locale)}
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        </section>}

        {/* --- tables ------------------------------------------------- */}
        <section>
          <Label className="mb-1.5">{t('about.whatIsParsed')}</Label>
          <Panel className="divide-y divide-[var(--border)]">
            {data.tables
              .filter((tb) => !tb.empty)
              .map((tb) => (
                <Row key={tb.name} label={<Untranslated>{tb.name}</Untranslated>} value={count(tb.rows, locale)} />
              ))}
          </Panel>
        </section>

        <section>
          <Label className="mb-1.5">{t('about.emptyTables')}</Label>
          <Panel className="divide-y divide-[var(--border)]">
            {emptyTables.length === 0 ? (
              <Row label={t('common.none')} value="" />
            ) : (
              emptyTables.map((tb) => (
                <Row key={tb.name} label={<Untranslated>{tb.name}</Untranslated>} value="0" muted />
              ))
            )}
          </Panel>
          <p className="mt-1 max-w-[70ch] text-[length:var(--text-micro)] leading-[var(--text-micro--line-height)] text-[var(--text-faint)]">
            {t('empty.governance')}
          </p>
        </section>

        {/* --- health ------------------------------------------------- */}
        <section className="lg:col-span-2">
          <Label className="mb-1.5">{t('about.concepts')}</Label>
          <Panel className="px-3 py-2.5">
            <div className="tnum flex flex-wrap gap-x-6 gap-y-1 text-[length:var(--text-data)]">
              <span>
                {count(data.concepts.concepts, locale)} concepts ·{' '}
                {count(data.concepts.aliases, locale)} aliases
              </span>
              <span className={data.concepts.factsWithConcept === 0 ? 'text-[var(--warn)]' : undefined}>
                {count(data.concepts.factsWithConcept, locale)} {t('common.of')}{' '}
                {count(data.concepts.facts, locale)} facts mapped
              </span>
            </div>
            {data.concepts.factsWithConcept === 0 && (
              <>
                <p className="mt-1.5 max-w-[86ch] text-[length:var(--text-tiny)] leading-[var(--text-tiny--line-height)] text-[var(--text-muted)]">
                  {t('fin.noConcepts')} {t('fin.noConceptsFix')}
                </p>
                <Command>python -m parser.concepts --seed</Command>
              </>
            )}
          </Panel>
        </section>

        <section className="lg:col-span-2">
          <Label className="mb-1.5">{t('about.indexes')}</Label>
          <Panel className="px-3 py-2.5">
            <div className="flex flex-wrap gap-2">
              {data.indexes.map((i) => (
                <Badge key={i.name} state={i.present ? 'pass' : 'warn'}>
                  <Untranslated>{i.name}</Untranslated>
                </Badge>
              ))}
            </div>
            {missingIndexes.length > 0 && (
              <>
                <p className="mt-1.5 text-[length:var(--text-tiny)] text-[var(--text-muted)]">
                  {missingIndexes.length} missing — the viewer works without them, slowly.
                </p>
                <Command>npm run db:indexes</Command>
              </>
            )}
          </Panel>
        </section>

        {data.orphanSymbols.length > 0 && (
          <section className="lg:col-span-2">
            <Label className="mb-1.5">{t('about.orphans')}</Label>
            <Panel className="px-3 py-2.5">
              <div className="flex flex-wrap gap-1.5">
                {data.orphanSymbols.map((s) => (
                  <Untranslated key={s}>{s}</Untranslated>
                ))}
              </div>
            </Panel>
          </section>
        )}

        <section className="lg:col-span-2">
          <p className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('about.lastRun')}: {timestamp(data.lastRunAt, locale)}
            <Sep />
            <Link href="/filter" className="link-inline">{t('nav.explore')}</Link>
          </p>
        </section>
      </div>
    </Column>
  )
}

function Row({
  label, value, muted,
}: { label: React.ReactNode; value: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-1.5">
      <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">{label}</span>
      <span
        className={cn(
          'tnum text-[length:var(--text-data)]',
          muted ? 'text-[var(--text-faint)]' : 'font-medium text-[var(--text)]',
        )}
      >
        {value}
      </span>
    </div>
  )
}

const Sep = () => <span className="mx-1.5 text-[var(--border-strong)]">·</span>

function Column({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[72rem] px-4 pb-10 pt-5 sm:px-5">{children}</div>
}
